import styled from 'styled-components';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
  StrapiCaseStudyCard,
  StrapiCaseStudyCardProps,
} from '@/features/strapi/components/case-study-card';
import { breakpoints } from '@/components/responsive';
import { VSpace } from '@/components/spacing';
import { useDetachedState } from '@/components/hooks/use-detached-state';

export interface CaseStudiesWithProgressProps {
  initProgress?: number;
  data: StrapiCaseStudyCardProps[];
  countDownInSeconds?: number;
}

export function CaseStudiesWithProgress({
  initProgress = 0,
  data,
  countDownInSeconds = 8,
}: CaseStudiesWithProgressProps) {
  const allLogos = useMemo(() => data.map((cs) => cs.logoImage), [data]);
  const count = useMemo(() => data.length, [data]);
  const scrollerRef = useRef(null);

  const [, setProgress, progressRef] = useDetachedState(initProgress);

  const selectedIdx = useMemo(() => {
    const divider = 100 / count;
    const intValue = Math.floor(
      zeroAbs(progressRef.current - countDownInSeconds / count) / divider,
    );
    return intValue % count;
  }, [progressRef.current]);

  const selectedCaseStudy = useMemo(() => data[selectedIdx], [selectedIdx]);

  const [noAnim, setNoAnim] = useState(false);

  const setCurrentIdx = (idx: number) => {
    const divider = 100 / count;
    setNoAnim(true);
    setProgress(idx * divider + countDownInSeconds / count);
    setTimeout(() => {
      setNoAnim(false);
    }, 1000);
  };

  const getProgressByIdx = useCallback(
    (idx: number) => {
      const divider = 100 / count;
      const intValue = Math.floor(progressRef.current / divider);
      const remainderValue = Math.floor(progressRef.current % divider);

      if (intValue > idx) {
        return 100;
      }
      if (intValue === idx) {
        return remainderValue * count;
      }
      return 0;
    },
    [progressRef.current, noAnim],
  );

  const onTick = () => {
    if (!noAnim) {
      if (progressRef.current < 100) {
        setProgress(progressRef.current + countDownInSeconds / count);
      } else {
        setNoAnim(true);
        setProgress(0);
        setTimeout(() => setNoAnim(false), 1000);
      }
    }
  };

  useEffect(() => {
    onTick();
    const interval = setInterval(onTick, 1000);
    return () => clearInterval(interval);
  }, [noAnim]);

  const scrollToIdx = (idx: number) => {
    if (scrollerRef?.current?.scrollTo) {
      scrollerRef?.current?.scrollTo(idx * window.innerWidth, 0);
    }
  };

  useEffect(() => {
    scrollToIdx(selectedIdx);
  }, [selectedIdx]);

  const onScroll = (ev) => {
    setCurrentIdx(Math.floor(ev.target.scrollLeft / window.innerWidth));
  };

  const onScrollDebounced = useDebouncedCallback(onScroll, 500);

  const onTouchDown = () => {
    setNoAnim(true);
  };
  const onTouchUp = () => {
    setNoAnim(false);
  };

  useEffect(() => {
    if (scrollerRef) {
      const scroller = scrollerRef?.current as HTMLDivElement;
      scroller?.addEventListener(`scroll`, onScrollDebounced);
      scroller?.addEventListener(`touchstart`, onTouchDown);
      scroller?.addEventListener(`touchend`, onTouchUp);
    }
    return () => {
      if (scrollerRef) {
        const scroller = scrollerRef?.current as HTMLDivElement;
        scroller?.removeEventListener(`scroll`, onScrollDebounced);
        scroller?.removeEventListener(`touchstart`, onTouchDown);
        scroller?.removeEventListener(`touchend`, onTouchUp);
      }
    };
  }, [scrollerRef]);

  return (
    <Wrapper>
      <CaseStudySelector className="only-desktop">
        {allLogos.map((logo, idx) => (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <img
            key={logo}
            className={idx === selectedIdx ? `selected` : ``}
            onClick={() => setCurrentIdx(idx)}
            src={logo}
            alt=""
          />
        ))}
      </CaseStudySelector>
      <VSpace value={3} mobileValue={1} />
      <ProgressWrapper count={count}>
        {allLogos.map((logo, idx) => (
          <ProgressOuter key={logo} onClick={() => setCurrentIdx(idx)}>
            <ProgressInner
              key={logo}
              progress={getProgressByIdx(idx)}
              noAnim={noAnim}
            />
          </ProgressOuter>
        ))}
      </ProgressWrapper>
      <VSpace value={3} className="only-desktop" />
      <VSpace value={2} className="only-mobile" />
      <MobileLogo
        key={selectedCaseStudy.logoImage}
        className="only-mobile"
        src={selectedCaseStudy.logoImage}
        alt=""
      />
      <VSpace value={1.5} className="only-mobile" />
      <CaseStudyWrapper className="only-desktop">
        <StrapiCaseStudyCard {...selectedCaseStudy} />
      </CaseStudyWrapper>
      <CaseStudyScroller ref={scrollerRef} width="93vw" className="only-mobile">
        {data.map((cs, idx) => (
          <div data-idx={idx} key={cs.title.text}>
            <StrapiCaseStudyCard key={cs.title.text} {...cs} />
          </div>
        ))}
      </CaseStudyScroller>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
`;

const CaseStudySelector = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: row;
  border-radius: 3rem;
  background-color: #ffffff;
  box-shadow: 0 7px 42px 0 rgba(253, 118, 0, 0.16);
  padding: 0rem 2rem;

  img {
    width: 17rem;
    padding: 2rem;
    object-fit: contain;
    margin: 0 2rem;
    opacity: 0.3;
    cursor: pointer;
  }

  img:hover {
    opacity: 0.7;
  }

  img.selected {
    opacity: 1;
    filter: ;
  }
`;

const MobileLogo = styled.img`
  max-width: 35vw;
  max-height: 6vh;
  min-height: 6vh;
  object-fit: contain;
`;

const CaseStudyWrapper = styled.div`
  display: grid;
  justify-items: center;

  max-width: 85%;

  ${breakpoints.down(`md`)} {
    max-width: 100%;
  }

  ${breakpoints.up(`xlr`)} {
    max-width: 130rem;
  }

  && > * {
    width: 100%;
  }
`;

const CaseStudyScroller = styled.div`
  display: grid;
  grid-auto-flow: column;
  overflow-x: scroll;
  max-width: 100vw;

  scroll-snap-type: x mandatory;
  scroll-snap-align: center;
  -webkit-overflow-scrolling: touch;

  & > * {
    scroll-behavior: smooth;
    scroll-snap-align: center;
    width: ${(props) => props.width || `auto`};
    margin-right: ${(props) => props.gap || `4rem`};
  }

  & > *:first-child {
    margin-left: ${(props) => props.gap || `4rem`};
  }
`;

const ProgressWrapper = styled.div`
  width: 55%;
  display: grid;
  grid-template-columns: repeat(${(props) => props.count}, 1fr);
  grid-gap: 1rem;

  ${breakpoints.down(`md`)} {
    width: 65%;
  }
`;

const ProgressOuter = styled.div`
  height: 0.4rem;
  width: ${(props: { width?: string }) => props.width || `100%`};
  border-radius: 3rem;
  background-color: rgba(0, 0, 0, 0.07);

  ${breakpoints.up(`xlr`)} {
    max-width: 80rem;
  }
`;

const ProgressInner = styled.div`
  height: 100%;
  border-radius: 3rem;
  background-color: rgba(0, 0, 0, 0.1);

  ${breakpoints.down(`md`)} {
    background-color: rgba(59, 71, 255, 0.69);
  }

  box-shadow: 0 7px 42px 0 rgba(253, 118, 0, 0.16);
  width: ${(props: { progress: number; noAnim: number }) =>
    props.progress > 0 ? `${props.progress}%` : `0%`};
  ${(props: { progress: number; noAnim: number }) =>
    !props.noAnim ? `transition: width 1s linear;` : `transition: none`};
`;

const zeroAbs = (n: number) => (n < 0 ? 0 : n);
